import {AfterViewInit,Component,OnDestroy,OnInit} from "@angular/core";
import {ActivatedRoute,NavigationExtras,Router} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import {FloatingButtonAction,TypeAction} from "@share/component/floating-button/floating-button";
import {PageHeaderItem} from "@share/component/page-header/page-header";
import {BehaviorSubject,Subscription} from "rxjs";
import {TypeReferentiel} from "@domain/admin/referentiels/type-referentiel";
import {ReferentielDto} from "@domain/admin/referentiels/referentielDto";
import {Onglets} from "@components/admin/frais/referentiel/onglets";
import {IComponentWithRoutedTabs} from "@domain/admin/recherche/component-with-routed-tabs";
import {FraisReferentielService} from "@components/admin/frais/referentiel/frais-referentiel.service";

/**
 * Composant d'affichage de la liste des référentiels de la gestion des frais
 */
@Component({
	host: {'data-test-id': 'frais-referentiel'},
	templateUrl: './frais-referentiel.component.html',
	providers: [FraisReferentielService],
})
export class FraisReferentielComponent implements OnInit, OnDestroy,AfterViewInit,IComponentWithRoutedTabs {
	/** Liste statique des onglets */
	public static listeOnglets: Array<PageHeaderItem> = [
		{
			code: Onglets.FAMILLE,
			libelle: 'admin.frais.referentiels.types.famille',
			url: 'Admin/Frais/Referentiels/Famille'
		},{
			code: Onglets.INDEMNITE,
			libelle: 'admin.frais.referentiels.types.domaine',
			url: 'Admin/Frais/Referentiels/Indemnite'
		},{
			code: Onglets.LIASSE,
			libelle: 'admin.frais.referentiels.types.liasse',
			url: 'Admin/Frais/Referentiels/Liasse'
		},{
			code: Onglets.TYPE_PRESTATION,
			libelle: 'admin.frais.referentiels.types.typePrestation',
			url: 'Admin/Frais/Referentiels/TypePrestation'
		},{
			code: Onglets.RUBRIQUE,
			libelle: 'admin.frais.referentiels.types.rubrique',
			url: 'Admin/Frais/Referentiels/Rubrique'
		},{
			code: Onglets.UNITE,
			libelle: 'admin.frais.referentiels.types.unite',
			url: 'Admin/Frais/Referentiels/Unite'
		},{
			code: Onglets.DISTANCE,
			libelle: 'admin.frais.referentiels.types.distance',
			url: 'Admin/Frais/Referentiels/Distance'
		},{
			code: Onglets.CODES_MCC,
			libelle: 'admin.frais.referentiels.types.codesMcc',
			url: 'Admin/Frais/Referentiels/CodesMCC'
		}
	];

	/** Liste des actions possibles */
	listeActions: BehaviorSubject<Array<FloatingButtonAction>> = new BehaviorSubject<Array<FloatingButtonAction>>(null);

	/** Liste des différents onglets disponibles dans le menu "Référentiels" de l'entreprise */
	listeTabItems: Array<PageHeaderItem> = FraisReferentielComponent.listeOnglets;

	/** Onglet courant */
	selectedItem$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

	/** Onglet cible lors du retour sur cet écran */
	targetTab: PageHeaderItem = null;

	/** Souscription */
	private subscription: Subscription;

	/** Déclarations pour le template */
	Onglets = Onglets;
	TypeReferentiel = TypeReferentiel;

	/**
	 * Constructeur
	 *
	 * @param translateService Service de traduction
	 * @param activatedRoute Service de routage
	 * @param router Router de l'application
	 */
	constructor(
		protected translateService: TranslateService,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		public fraisReferentielService: FraisReferentielService,
	) {
		if (this.router.getCurrentNavigation()?.extras?.state?.referentiel) {
			//S'il y a un référentiel dans le state, c'est que l'on vient de le créer
			const referentiel: ReferentielDto = this.router.getCurrentNavigation()?.extras?.state?.referentiel;

			//Suivant le type de ce référentiel, on définit le targetTab
			switch (referentiel.type) {
				case TypeReferentiel.FAMILLE:
					this.targetTab = this.listeTabItems.find(tab => tab.code === Onglets.FAMILLE);
					break;
				case TypeReferentiel.INDEMNITE:
					this.targetTab = this.listeTabItems.find(tab => tab.code === Onglets.INDEMNITE);
					break;
				case TypeReferentiel.LIASSE:
					this.targetTab = this.listeTabItems.find(tab => tab.code === Onglets.LIASSE);
					break;
				case TypeReferentiel.TYPE_PRESTATION:
					this.targetTab = this.listeTabItems.find(tab => tab.code === Onglets.TYPE_PRESTATION);
					break;
				case TypeReferentiel.RUBRIQUE:
					this.targetTab = this.listeTabItems.find(tab => tab.code === Onglets.RUBRIQUE);
					break;
				case TypeReferentiel.NS_UNITE:
					this.targetTab = this.listeTabItems.find(tab => tab.code === Onglets.UNITE);
					break;
				case TypeReferentiel.NS_UNITE_DISTANCE:
					this.targetTab = this.listeTabItems.find(tab => tab.code === Onglets.DISTANCE);
					break;
				case TypeReferentiel.CODES_MCC:
					this.targetTab = this.listeTabItems.find(tab => tab.code === Onglets.CODES_MCC);
					break;
			}
		} else {
			//On récupère un onglet à rejoindre s'il y en a un
			this.targetTab = this.router.getCurrentNavigation()?.extras?.state?.tab;
		}
	}

	/**
	 * Initialisation du composant
	 */
	ngOnInit() {
		this.subscription = this.activatedRoute.url.subscribe(() => {
			//Récupération de la route
			const firstChild = this.activatedRoute.snapshot.firstChild;

			//Extraction du path
			const path = firstChild.url[0].path;

			//On vérifie si on trouve l'onglet demandé via l'url
			const matching = this.listeTabItems.find(i => i.code === path);

			//Si on trouve bien l'onglet correspondant
			if (!!matching) {
				//on met à jour le composant header
				setTimeout(() => {
					this.targetTab = matching;
					this.selectedItem$.next((this.listeTabItems.findIndex(item => item.code === this.targetTab.code)));
				});
			} else {
				//Sinon, on redirige sur le premier onglet et on met à jour le composant header
				setTimeout(() => {
					this.targetTab = this.listeTabItems[0];
					this.selectedItem$.next((this.listeTabItems.findIndex(item => item.code === this.targetTab.code)));
				});
			}
		});

		//Définition de la liste des actions du bouton en bas à droite de l'écran
		this.listeActions.next([{
			type: TypeAction.PRIMARY,
			icone: 'nio icon-ajouter',
			libelle: 'global.actions.creer',
			doAction: () => this.createNewReferentiel()
		}]);
	}

	/**
	 * Après l'initialisation de la vue
	 */
	ngAfterViewInit(): void {
		//Si un onglet cible a été récupéré
		if (this.targetTab) {
			//On switch sur celui-ci
			this.selectedItem$.next((this.listeTabItems.findIndex(item => item.code === this.targetTab.code)));
		}
	}

	/**
	 * Méthode appelée lors de la création d'un nouveau référentiel
	 */
	createNewReferentiel() {
		//Définition des extras
		const extras: NavigationExtras = {
			state: {
				tab: this.listeTabItems[this.selectedItem$.value]
			}
		}

		//Navigation vers la page d'informations du référentiel
		this.router.navigate(['Admin/Frais/Referentiel/-1/false/-1'],extras);
	}

	/**
	 * Changement d'onglet
	 *
	 * @param selectedItem Onglet sélectionné
	 */
	onSelectedItemChange(selectedItem: PageHeaderItem): void {
		//Mise à jour de l'onglet sélectionné
		this.selectedItem$.next(this.listeTabItems.findIndex(item => item.code === selectedItem.code))

		const typeReferentiel = FraisReferentielComponent.getTypeReferentielByOnglet(selectedItem.code);

		this.fraisReferentielService.loadListView(typeReferentiel);

		//Si un onglet est sélectionné
		if (!this.targetTab) {
			//Application de l'onglet
			this.targetTab = selectedItem;
		}

		//Si l'onglet a changé
		if (selectedItem?.code !== this.targetTab?.code) {
			//Application de l'onglet
			this.targetTab = selectedItem;
			this.router.navigateByUrl(selectedItem.url);
		}
	}

	/**
	 * Destruction du composant
	 */
	ngOnDestroy() {
		//Résiliation de l'abonnement
		this.subscription.unsubscribe();
	}

	/**
	 * Renvoie le titre à donner à la liste pour un onglet donné
	 *
	 * @param onglet le code d'un onglet
	 */
	static getTitleByType(typeReferentiel: TypeReferentiel): string {
		switch (typeReferentiel) {
			case TypeReferentiel.FAMILLE:
				return 'admin.frais.referentiels.types.famille';
			case TypeReferentiel.INDEMNITE:
				return 'admin.frais.referentiels.types.domaine';
			case TypeReferentiel.LIASSE:
				return 'admin.frais.referentiels.types.liasse';
			case TypeReferentiel.TYPE_PRESTATION:
				return 'admin.frais.referentiels.types.typePrestation'
			case TypeReferentiel.RUBRIQUE:
				return 'admin.frais.referentiels.types.rubrique';
			case TypeReferentiel.NS_UNITE:
				return 'admin.frais.referentiels.types.unite';
			case TypeReferentiel.NS_UNITE_DISTANCE:
				return 'admin.frais.referentiels.types.distance';
			case TypeReferentiel.CODES_MCC:
				return 'admin.frais.referentiels.types.codesMcc';
		}
	};

	/**
	 * Renvoie le type de référentiel associé à un onglet donné
	 *
	 * @param onglet le code d'un onglet
	 */
	static getTypeReferentielByOnglet(onglet: string): TypeReferentiel {
		switch (onglet) {
			case Onglets.FAMILLE:
				return TypeReferentiel.FAMILLE;
			case Onglets.INDEMNITE:
				return TypeReferentiel.INDEMNITE;
			case Onglets.LIASSE:
				return TypeReferentiel.LIASSE;
			case Onglets.TYPE_PRESTATION:
				return TypeReferentiel.TYPE_PRESTATION;
			case Onglets.RUBRIQUE:
				return TypeReferentiel.RUBRIQUE;
			case Onglets.UNITE:
				return TypeReferentiel.NS_UNITE;
			case Onglets.DISTANCE:
				return TypeReferentiel.NS_UNITE_DISTANCE;
			case Onglets.CODES_MCC:
				return TypeReferentiel.CODES_MCC;
		}
	};
}
